// `agentId: <id>` in an Agent/Task launch result names the subagent a later SendMessage `to` addresses. Later sub_agent
// rows carry no output, so the id is captured off the launch row (entry.subagentId / metadata.subagentId).
import { headerLabel } from "./tool-call-label.js";

const SUBAGENT_ID_RE = /\bagentId:\s*([\w-]+)/;

// Scan every string leaf of the output (a JSON dump would escape the newline).
function idIn(v: unknown, depth = 0): string | undefined {
  if (typeof v === "string") return SUBAGENT_ID_RE.exec(v)?.[1];
  if (depth > 4 || v === null || typeof v !== "object") return undefined;
  for (const x of Object.values(v)) {
    const id = idIn(x, depth + 1);
    if (id) return id;
  }
  return undefined;
}

export function launchedSubagentId(
  detail: { type?: unknown; output?: unknown } | null | undefined,
): string | undefined {
  return detail?.type === "unknown" ? idIn(detail.output) : undefined;
}

const FINISHED_LINE_CALL_PREFIX = "task_notification_subagent_";

/** The subagent a finished line is about. The callId is durable; metadata may not survive every hop. */
export function finishedLineSubagentId(entry: {
  callId: string;
  subagentId?: string;
}): string | undefined {
  if (entry.subagentId) return entry.subagentId;
  return entry.callId.startsWith(FINISHED_LINE_CALL_PREFIX)
    ? entry.callId.slice(FINISHED_LINE_CALL_PREFIX.length) || undefined
    : undefined;
}

/** The id a tool_call item carries: the server's metadata stamp, else its own launch output (older rows). */
export function subagentIdOfItem(item: {
  detail?: unknown;
  metadata?: unknown;
}): string | undefined {
  const id = (item.metadata as { subagentId?: unknown } | undefined)?.subagentId;
  return typeof id === "string"
    ? id
    : launchedSubagentId(item.detail as { type?: unknown; output?: unknown });
}

const rec = (v: unknown): Record<string, unknown> | undefined =>
  typeof v === "object" && v !== null && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : undefined;
const text = (v: unknown): string | undefined =>
  typeof v === "string" && v.trim() ? v : undefined;

/** The launch card's name: the Task's description, else its type (kept as entry.subagentLabel / metadata.subagentLabel). */
export function subagentLabelOfItem(item: {
  detail?: unknown;
  metadata?: unknown;
}): string | undefined {
  const d = rec(item.detail);
  const input = rec(d?.input);
  const stamped = rec(item.metadata)?.subagentLabel;
  return (
    headerLabel(
      text(input?.description) ??
        text(d?.description) ??
        text(stamped) ??
        text(input?.subagent_type) ??
        text(d?.subAgentType),
    ) ?? undefined
  );
}
